'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  PieChart,
  BarChart3,
  DollarSign,
  Percent,
  AlertCircle,
  Info,
  Eye,
  EyeOff,
  Calendar,
  Target
} from 'lucide-react'
import { formatCurrency } from '../utils/currency'
import { BirhausCurrencyDisplay } from './BirhausCurrencyDisplay'
import type { 
  BirhausInvestmentPortfolioProps, 
  Investment,
  InvestmentType,
  InvestmentRisk,
  PortfolioAllocation 
} from '../types/financial'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// Investment type configuration
const investmentTypeConfig: Record<InvestmentType, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  acciones: {
    icon: <TrendingUp size={20} />,
    colorClass: 'text-blue-600 bg-blue-100',
    nameEs: 'Acciones',
    nameEn: 'Stocks'
  },
  bonos: {
    icon: <BarChart3 size={20} />,
    colorClass: 'text-green-600 bg-green-100',
    nameEs: 'Bonos',
    nameEn: 'Bonds'
  },
  fondo_mutuo: {
    icon: <PieChart size={20} />,
    colorClass: 'text-purple-600 bg-purple-100',
    nameEs: 'Fondos Mutuos',
    nameEn: 'Mutual Funds'
  },
  etf: {
    icon: <BarChart3 size={20} />,
    colorClass: 'text-orange-600 bg-orange-100',
    nameEs: 'ETF',
    nameEn: 'ETF'
  },
  crypto: {
    icon: <DollarSign size={20} />,
    colorClass: 'text-yellow-600 bg-yellow-100',
    nameEs: 'Crypto',
    nameEn: 'Crypto'
  },
  criptomonedas: {
    icon: <DollarSign size={20} />,
    colorClass: 'text-yellow-600 bg-yellow-100',
    nameEs: 'Criptomonedas',
    nameEn: 'Cryptocurrency'
  },
  plazo_fijo: {
    icon: <Calendar size={20} />,
    colorClass: 'text-indigo-600 bg-indigo-100',
    nameEs: 'Plazo Fijo',
    nameEn: 'Fixed Deposit'
  },
  inmuebles: {
    icon: <Target size={20} />,
    colorClass: 'text-red-600 bg-red-100',
    nameEs: 'Inmuebles',
    nameEn: 'Real Estate'
  }
}

// Risk level configuration
const riskLevelConfig: Record<InvestmentRisk, {
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  bajo: {
    colorClass: 'text-green-700 bg-green-100',
    nameEs: 'Riesgo Bajo',
    nameEn: 'Low Risk'
  },
  medio: {
    colorClass: 'text-yellow-700 bg-yellow-100',
    nameEs: 'Riesgo Medio',
    nameEn: 'Medium Risk'
  },
  alto: {
    colorClass: 'text-red-700 bg-red-100',
    nameEs: 'Riesgo Alto',
    nameEn: 'High Risk'
  }
}

/**
 * BirhausInvestmentPortfolio - Investment portfolio tracking component
 * 
 * Features:
 * - Multiple investment types with appropriate icons and colors
 * - Real-time performance tracking with gains/losses
 * - Risk level indicators and portfolio diversification analysis
 * - Portfolio allocation visualization and recommendations
 * - Spanish-first investment terminology
 * - Miller's Law compliance (max 7 visible investments)
 * - Privacy toggle for sensitive financial information
 * - Accessibility compliant with proper ARIA labels
 * - Responsive layout for different screen sizes
 * - Performance alerts and investment recommendations
 */
export function BirhausInvestmentPortfolio({
  investments,
  showPrivacyToggle = true,
  showAllocation = true,
  showPerformanceMetrics = true,
  moneda = 'PYG',
  onInvestmentClick,
  className,
  ...props
}: BirhausInvestmentPortfolioProps) {
  const { language, reportViolation } = useBirhaus()
  const [isPrivacyEnabled, setIsPrivacyEnabled] = React.useState(false)

  // BIRHAUS validation: Check cognitive load (max 7 investments - Miller's Law)
  React.useEffect(() => {
    if (investments.length > 7) {
      reportViolation({
        type: 'cognitive',
        severity: 'warning',
        element: document.createElement('div'),
        message: `Investment portfolio shows ${investments.length} investments (max recommended: 7)`,
        messageEs: `Portafolio de inversiones muestra ${investments.length} inversiones (máximo recomendado: 7)`,
        messageEn: `Investment portfolio shows ${investments.length} investments (max recommended: 7)`,
        recommendation: 'Consider grouping similar investments or using pagination',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [investments.length, reportViolation])

  // Calculate portfolio totals and performance
  const portfolioMetrics = React.useMemo(() => {
    const totalValue = investments.reduce((sum: number, inv: Investment) => sum + inv.valorActual, 0)
    const totalCost = investments.reduce((sum: number, inv: Investment) => sum + inv.montoInvertido, 0)
    const totalGainLoss = totalValue - totalCost
    const totalGainLossPercentage = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0

    // Calculate allocation by type
    const allocationByType = investments.reduce((acc, inv) => {
      const existing = acc.find(item => item.tipo === inv.tipo)
      if (existing) {
        existing.valor += inv.valorActual
        existing.porcentaje = (existing.valor / totalValue) * 100
      } else {
        acc.push({
          tipo: inv.tipo,
          valor: inv.valorActual,
          porcentaje: (inv.valorActual / totalValue) * 100,
          rendimiento: inv.rendimiento
        })
      }
      return acc
    }, [] as PortfolioAllocation[])

    // Calculate allocation by risk level
    const allocationByRisk = investments.reduce((acc, inv) => {
      const existing = acc.find(item => item.nivel === inv.nivelRiesgo)
      if (existing) {
        existing.valor += inv.valorActual
        existing.porcentaje = (existing.valor / totalValue) * 100
      } else {
        acc.push({
          nivel: inv.nivelRiesgo ?? inv.riesgo,
          valor: inv.valorActual,
          porcentaje: (inv.valorActual / totalValue) * 100
        })
      }
      return acc
    }, [] as { nivel: InvestmentRisk; valor: number; porcentaje: number }[])

    return {
      totalValue,
      totalCost,
      totalGainLoss,
      totalGainLossPercentage,
      allocationByType,
      allocationByRisk
    }
  }, [investments])

  // BIRHAUS validation: Check for high-risk concentration
  React.useEffect(() => {
    const highRiskAllocation = portfolioMetrics.allocationByRisk
      .find(item => item.nivel === 'alto')?.porcentaje || 0

    if (highRiskAllocation > 30) {
      reportViolation({
        type: 'performance',
        severity: 'warning',
        element: document.createElement('div'),
        message: `High-risk investments represent ${highRiskAllocation.toFixed(1)}% of portfolio`,
        messageEs: `Inversiones de alto riesgo representan ${highRiskAllocation.toFixed(1)}% del portafolio`,
        messageEn: `High-risk investments represent ${highRiskAllocation.toFixed(1)}% of portfolio`,
        recommendation: 'Consider diversifying with lower-risk investments',
        birhausPrinciple: 6 // Progressive disclosure of risk information
      })
    }
  }, [portfolioMetrics.allocationByRisk, reportViolation])

  // Toggle privacy mode
  const togglePrivacy = React.useCallback(() => {
    setIsPrivacyEnabled(!isPrivacyEnabled)
  }, [isPrivacyEnabled])

  // Handle investment click
  const handleInvestmentClick = React.useCallback((investment: Investment) => {
    onInvestmentClick?.(investment)
  }, [onInvestmentClick])

  // Format display value
  const formatDisplayValue = (value: number, currency: string = moneda) => {
    if (isPrivacyEnabled) {
      return '••••••••'
    }
    return <BirhausCurrencyDisplay amount={value} currency={currency as any} />
  }

  // Get performance color class
  const getPerformanceColorClass = (gainLoss: number) => {
    if (gainLoss > 0) return 'text-green-600'
    if (gainLoss < 0) return 'text-red-600'
    return 'text-birhaus-muted-foreground'
  }

  if (investments.length === 0) {
    return (
      <div className={cn('p-8 text-center border border-birhaus-border rounded-lg', className)} {...props}>
        <PieChart size={48} className="mx-auto mb-4 text-birhaus-muted-foreground/50" />
        <h3 className="text-lg font-semibold text-birhaus-foreground mb-2">
          {language === 'es' ? 'No hay inversiones en el portafolio' : 'No investments in portfolio'}
        </h3>
        <p className="text-birhaus-muted-foreground">
          {language === 'es' 
            ? 'Agrega tu primera inversión para comenzar a hacer seguimiento de tu portafolio.'
            : 'Add your first investment to start tracking your portfolio.'
          }
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Portfolio Summary Header */}
      <div className="bg-gradient-to-r from-birhaus-primary to-birhaus-primary/80 p-6 rounded-lg text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold mb-1">
              {language === 'es' ? 'Portafolio de Inversiones' : 'Investment Portfolio'}
            </h2>
            <p className="text-white/80">
              {investments.length} {language === 'es' ? 'inversiones' : 'investments'}
            </p>
          </div>
          
          {showPrivacyToggle && (
            <button
              onClick={togglePrivacy}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              aria-label={isPrivacyEnabled 
                ? (language === 'es' ? 'Mostrar valores' : 'Show values')
                : (language === 'es' ? 'Ocultar valores' : 'Hide values')}
            >
              {isPrivacyEnabled ? <EyeOff size={16} /> : <Eye size={16} />}
              <span className="hidden sm:inline">
                {isPrivacyEnabled 
                  ? (language === 'es' ? 'Mostrar' : 'Show')
                  : (language === 'es' ? 'Ocultar' : 'Hide')
                }
              </span>
            </button>
          )}
        </div>

        {/* Portfolio Totals */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-white/70 text-sm mb-1">
              {language === 'es' ? 'Valor Total' : 'Total Value'}
            </p>
            <p className="text-xl font-bold">
              {formatDisplayValue(portfolioMetrics.totalValue)}
            </p>
          </div>
          
          <div>
            <p className="text-white/70 text-sm mb-1">
              {language === 'es' ? 'Ganancia/Pérdida' : 'Gain/Loss'}
            </p>
            <p className={cn(
              'text-xl font-bold',
              isPrivacyEnabled ? 'text-white' : getPerformanceColorClass(portfolioMetrics.totalGainLoss)
            )}>
              {isPrivacyEnabled ? '••••••••' : (
                <>
                  {portfolioMetrics.totalGainLoss >= 0 ? '+' : ''}
                  <BirhausCurrencyDisplay 
                    amount={portfolioMetrics.totalGainLoss} 
                    currency={moneda}
                    className={getPerformanceColorClass(portfolioMetrics.totalGainLoss)}
                  />
                </>
              )}
            </p>
          </div>
          
          <div>
            <p className="text-white/70 text-sm mb-1">
              {language === 'es' ? 'Rendimiento %' : 'Performance %'}
            </p>
            <p className={cn(
              'text-xl font-bold flex items-center gap-2',
              isPrivacyEnabled ? 'text-white' : getPerformanceColorClass(portfolioMetrics.totalGainLoss)
            )}>
              {isPrivacyEnabled ? '••••' : (
                <>
                  {portfolioMetrics.totalGainLossPercentage > 0 ? (
                    <TrendingUp size={20} />
                  ) : portfolioMetrics.totalGainLossPercentage < 0 ? (
                    <TrendingDown size={20} />
                  ) : (
                    <Minus size={20} />
                  )}
                  {portfolioMetrics.totalGainLossPercentage >= 0 ? '+' : ''}
                  {portfolioMetrics.totalGainLossPercentage.toFixed(2)}%
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Portfolio Allocation */}
      {showAllocation && !isPrivacyEnabled && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Allocation by Type */}
          <div className="bg-white p-6 border border-birhaus-border rounded-lg">
            <h3 className="text-lg font-semibold text-birhaus-foreground mb-4">
              {language === 'es' ? 'Asignación por Tipo' : 'Allocation by Type'}
            </h3>
            <div className="space-y-3">
              {portfolioMetrics.allocationByType.map((allocation) => {
                const typeConfig = investmentTypeConfig[allocation.tipo]
                return (
                  <div key={allocation.tipo} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn('p-1 rounded', typeConfig.colorClass)}>
                        {typeConfig.icon}
                      </div>
                      <span className="text-sm font-medium">
                        {language === 'es' ? typeConfig.nameEs : typeConfig.nameEn}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">
                        {allocation.porcentaje.toFixed(1)}%
                      </p>
                      <p className="text-xs text-birhaus-muted-foreground">
                        {formatDisplayValue(allocation.valor)}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Allocation by Risk */}
          <div className="bg-white p-6 border border-birhaus-border rounded-lg">
            <h3 className="text-lg font-semibold text-birhaus-foreground mb-4">
              {language === 'es' ? 'Asignación por Riesgo' : 'Allocation by Risk'}
            </h3>
            <div className="space-y-3">
              {portfolioMetrics.allocationByRisk.map((allocation) => {
                const riskConfig = riskLevelConfig[allocation.nivel]
                return (
                  <div key={allocation.nivel} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        riskConfig.colorClass
                      )}>
                        {language === 'es' ? riskConfig.nameEs : riskConfig.nameEn}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">
                        {allocation.porcentaje.toFixed(1)}%
                      </p>
                      <p className="text-xs text-birhaus-muted-foreground">
                        {formatDisplayValue(allocation.valor)}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Individual Investments */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-birhaus-foreground">
          {language === 'es' ? 'Inversiones Individuales' : 'Individual Investments'}
        </h3>
        
        {investments.map((investment) => {
          const typeConfig = investmentTypeConfig[investment.tipo]
          const riesgoLevel = investment.nivelRiesgo ?? investment.riesgo
          const riskConfig = riskLevelConfig[riesgoLevel]
          const costoBase = investment.costoBase ?? investment.montoInvertido
          const gainLoss = investment.valorActual - costoBase
          const gainLossPercentage = costoBase > 0 ? (gainLoss / costoBase) * 100 : 0
          const isClickable = Boolean(onInvestmentClick)

          return (
            <div
              key={investment.id}
              className={cn(
                'p-4 border border-birhaus-border rounded-lg transition-all duration-200',
                isClickable && 'cursor-pointer hover:border-birhaus-primary/50 hover:shadow-sm',
                'focus:outline-none focus:ring-2 focus:ring-birhaus-primary focus:border-birhaus-primary'
              )}
              onClick={() => isClickable && handleInvestmentClick(investment)}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  handleInvestmentClick(investment)
                }
              }}
              tabIndex={isClickable ? 0 : -1}
              role={isClickable ? 'button' : undefined}
            >
              <div className="flex items-start justify-between">
                {/* Investment info */}
                <div className="flex items-start gap-4 flex-1">
                  <div className={cn('p-2 rounded-full', typeConfig.colorClass)}>
                    {typeConfig.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-birhaus-foreground truncate">
                        {investment.nombre ?? investment.nombreEs}
                      </h4>
                      <span className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        riskConfig.colorClass
                      )}>
                        {language === 'es' ? riskConfig.nameEs : riskConfig.nameEn}
                      </span>
                    </div>

                    <p className="text-sm text-birhaus-muted-foreground mb-2">
                      {language === 'es' ? typeConfig.nameEs : typeConfig.nameEn}
                      {investment.simbolo && ` • ${investment.simbolo}`}
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-birhaus-muted-foreground mb-1">
                          {language === 'es' ? 'Cantidad' : 'Quantity'}
                        </p>
                        <p className="font-medium">
                          {investment.cantidad?.toLocaleString() ?? '-'}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-birhaus-muted-foreground mb-1">
                          {language === 'es' ? 'Costo Base' : 'Cost Basis'}
                        </p>
                        <p className="font-medium">
                          {formatDisplayValue(costoBase)}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-birhaus-muted-foreground mb-1">
                          {language === 'es' ? 'Valor Actual' : 'Current Value'}
                        </p>
                        <p className="font-medium">
                          {formatDisplayValue(investment.valorActual)}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-birhaus-muted-foreground mb-1">
                          {language === 'es' ? 'Rendimiento' : 'Return'}
                        </p>
                        <p className={cn(
                          'font-medium flex items-center gap-1',
                          isPrivacyEnabled ? 'text-birhaus-foreground' : getPerformanceColorClass(gainLoss)
                        )}>
                          {!isPrivacyEnabled && (
                            <>
                              {gainLossPercentage > 0 ? (
                                <TrendingUp size={12} />
                              ) : gainLossPercentage < 0 ? (
                                <TrendingDown size={12} />
                              ) : (
                                <Minus size={12} />
                              )}
                              {gainLossPercentage >= 0 ? '+' : ''}
                              {gainLossPercentage.toFixed(2)}%
                            </>
                          )}
                          {isPrivacyEnabled && '••••'}
                        </p>
                      </div>
                    </div>

                    {/* Additional investment info */}
                    {(investment.fechaCompra ?? investment.fechaInversion) && (
                      <p className="text-xs text-birhaus-muted-foreground mt-2">
                        {language === 'es' ? 'Comprado: ' : 'Purchased: '}
                        {(investment.fechaCompra ?? investment.fechaInversion).toLocaleDateString(
                          language === 'es' ? 'es-PY' : 'en-US'
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Performance indicator */}
                {!isPrivacyEnabled && (
                  <div className="text-right flex-shrink-0">
                    <p className={cn('font-bold', getPerformanceColorClass(gainLoss))}>
                      {gainLoss >= 0 ? '+' : ''}
                      <BirhausCurrencyDisplay 
                        amount={gainLoss} 
                        currency={moneda}
                        className={getPerformanceColorClass(gainLoss)}
                      />
                    </p>
                    
                    {/* Warning for significant losses */}
                    {gainLossPercentage <= -20 && (
                      <div className="flex items-center gap-1 text-red-600 mt-1">
                        <AlertCircle size={12} />
                        <span className="text-xs">
                          {language === 'es' ? 'Pérdida significativa' : 'Significant loss'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Portfolio Performance Metrics */}
      {showPerformanceMetrics && !isPrivacyEnabled && (
        <div className="bg-birhaus-muted/10 p-6 rounded-lg border border-birhaus-border">
          <h3 className="text-lg font-semibold text-birhaus-foreground mb-4">
            {language === 'es' ? 'Métricas de Rendimiento' : 'Performance Metrics'}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-green-600">
                {investments.filter(inv => (inv.valorActual - (inv.costoBase ?? inv.montoInvertido)) > 0).length}
              </p>
              <p className="text-sm text-birhaus-muted-foreground">
                {language === 'es' ? 'Con Ganancias' : 'Profitable'}
              </p>
            </div>
            
            <div>
              <p className="text-2xl font-bold text-red-600">
                {investments.filter(inv => (inv.valorActual - (inv.costoBase ?? inv.montoInvertido)) < 0).length}
              </p>
              <p className="text-sm text-birhaus-muted-foreground">
                {language === 'es' ? 'Con Pérdidas' : 'Losing'}
              </p>
            </div>
            
            <div>
              <p className="text-2xl font-bold text-birhaus-foreground">
                {portfolioMetrics.allocationByType.length}
              </p>
              <p className="text-sm text-birhaus-muted-foreground">
                {language === 'es' ? 'Tipos de Activos' : 'Asset Types'}
              </p>
            </div>
            
            <div>
              <p className="text-2xl font-bold text-birhaus-foreground">
                {((portfolioMetrics.allocationByRisk.find(r => r.nivel === 'alto')?.porcentaje || 0)).toFixed(0)}%
              </p>
              <p className="text-sm text-birhaus-muted-foreground">
                {language === 'es' ? 'Alto Riesgo' : 'High Risk'}
              </p>
            </div>
          </div>

          {/* Diversification recommendation */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">
                  {language === 'es' ? 'Recomendación de Diversificación' : 'Diversification Recommendation'}
                </p>
                <p className="text-xs text-blue-800">
                  {language === 'es' 
                    ? 'Considera balancear tu portafolio con una mezcla de inversiones de bajo, medio y alto riesgo para optimizar el rendimiento.'
                    : 'Consider balancing your portfolio with a mix of low, medium, and high-risk investments to optimize performance.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export type { BirhausInvestmentPortfolioProps }